import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Palette, Loader2, Trash2, X, ImageIcon, Pencil, Check, Camera, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const ProjectMockups = ({ projectId, user }) => {
  const [mockups, setMockups] = useState([]);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [editingDesc, setEditingDesc] = useState(false);
  const [editText, setEditText] = useState('');

  const isAdmin = user.role === 'admin' || user.role === 'supervisor';

  const fetchMockups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/mockups`);
      setMockups(r.data);
    } catch (e) { /* not authorized or none */ }
  }, [projectId]);

  useEffect(() => { fetchMockups(); }, [fetchMockups]);

  const uploadMockups = async () => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (description.trim()) fd.append('description', description.trim());
      await axios.post(`${API}/projects/${projectId}/mockups`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFiles([]);
      setDescription('');
      toast.success(`${files.length} mockup${files.length !== 1 ? 's' : ''} uploaded`);
      fetchMockups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload mockups');
    } finally { setUploading(false); }
  };

  const deleteMockup = async (id) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/mockups/${id}`);
      toast.success('Mockup deleted');
      setViewer(null);
      fetchMockups();
    } catch (e) { toast.error('Failed to delete mockup'); }
  };

  const saveDescription = async () => {
    try {
      const r = await axios.put(`${API}/projects/${projectId}/mockups/${viewer.id}`, { text: editText });
      toast.success('Description updated');
      setViewer({ ...viewer, text: r.data.text });
      setEditingDesc(false);
      fetchMockups();
    } catch (e) { toast.error('Failed to update description'); }
  };

  const openViewer = (m) => {
    setEditingDesc(false);
    setViewer(m);
    if (user.role === 'client') {
      axios.post(`${API}/projects/${projectId}/mockups/${m.id}/view`).catch(() => {});
    }
  };

  if (!isAdmin && mockups.length === 0) return null;

  return (
    <>
    <div className="glass-card p-4 sm:p-6 mb-6" data-testid="project-mockups-section">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <Palette className="w-6 h-6 text-pink-400" />
        Project Photo Mockup
      </h2>

      {isAdmin && (
        <div className="space-y-2 p-3 rounded-lg bg-gray-100 border border-gray-200 mb-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 text-sm"
              data-testid="mockup-file-input"
            />
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm"
              data-testid="mockup-desc-input"
            />
            <Button
              onClick={uploadMockups}
              disabled={uploading || files.length === 0}
              className="bg-pink-600 hover:bg-pink-700 text-white min-h-[44px]"
              data-testid="mockup-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Camera className="w-4 h-4 mr-2" />Upload{files.length > 0 ? ` (${files.length})` : ''}</>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500">Mockups are visible to the client on their dashboard and this page.</p>
        </div>
      )}

      {mockups.length === 0 ? (
        <div className="text-center py-10 text-gray-400" data-testid="mockups-empty">
          <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No mockups yet{isAdmin ? ' — upload the first one above' : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {mockups.map((m) => (
            <button
              key={m.id}
              onClick={() => openViewer(m)}
              className="relative group rounded-lg overflow-hidden aspect-square border border-gray-200 bg-slate-800"
              data-testid={`mockup-photo-${m.id}`}
            >
              <img
                src={`${BACKEND_URL}${m.file_path}`}
                alt={m.text || 'Mockup'}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
              {isAdmin && Array.isArray(m.client_views) && m.client_views.length > 0 && (
                <span
                  className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1"
                  title={m.client_views
                    .map((v) => `${v.user_name} • ${v.view_count || 1} view${(v.view_count || 1) !== 1 ? 's' : ''} • last: ${new Date(v.viewed_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}`)
                    .join('\n')}
                  data-testid={`mockup-view-badge-${m.id}`}
                >
                  <Eye className="w-3 h-3" />
                  {m.total_views || m.client_views.reduce((s, v) => s + (v.view_count || 1), 0)}
                </span>
              )}
              {m.text && (
                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-xs px-2 py-1 truncate text-left">{m.text}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>

    {viewer && (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
        onClick={() => { setEditingDesc(false); setViewer(null); }}
        data-testid="mockup-viewer"
      >
        <div className="max-w-4xl w-full max-h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end gap-2 mb-2">
            {isAdmin && (
              <>
              <Button size="sm" variant="ghost" onClick={() => { setEditText(viewer.text || ''); setEditingDesc(true); }} style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', color: '#60a5fa' }} data-testid="mockup-edit-btn">
                <Pencil className="w-5 h-5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => deleteMockup(viewer.id)} style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', color: '#f87171' }} data-testid="mockup-delete-btn">
                <Trash2 className="w-5 h-5" />
              </Button>
              </>
            )}
            <Button size="sm" variant="ghost" onClick={() => { setEditingDesc(false); setViewer(null); }} style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', color: '#ffffff' }} data-testid="mockup-close-btn">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <img src={`${BACKEND_URL}${viewer.file_path}`} alt={viewer.text || 'Mockup'} className="w-full max-h-[70vh] object-contain rounded-lg" />
          <div
            className="mt-3 mx-auto px-4 py-2 rounded-lg text-center"
            style={{ backgroundColor: 'rgba(15, 23, 42, 0.92)', maxWidth: '92%' }}
          >
            {editingDesc ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="Mockup description..."
                  className="flex-1 bg-white border-2 border-slate-300 text-slate-900 text-sm"
                  data-testid="mockup-edit-input"
                />
                <Button size="sm" onClick={saveDescription} className="bg-green-600 hover:bg-green-700" style={{ color: '#ffffff' }} data-testid="mockup-save-desc-btn">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditingDesc(false)} className="border-slate-400">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                {viewer.text && <p className="text-base" style={{ color: '#ffffff' }}>{viewer.text}</p>}
                <p className="text-sm mt-1" style={{ color: '#cbd5e1' }}>
                  {new Date(viewer.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProjectMockups;
